import { Fragment, useState } from "react";
import { Card } from "@/components/ui/card";
import { ConstantSumSlider } from "./ConstantSumSlider";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Info, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";

interface Cluster {
  id: string;
  label: string;
  description?: string;
  image_url?: string;
  subCategories: SubCategory[];
}

interface SubCategory {
  id: string;
  label: string;
  description?: string;
}

interface PollConstantSumFormProps {
  clusters: Cluster[];
  allocations: Record<string, number>;
  subAllocations: Record<string, number>;
  onClusterChange: (clusterId: string, value: number) => void;
  onSubChange: (subId: string, value: number) => void;
  onClusterDragStart?: (clusterId: string) => void;
  onClusterDragEnd?: () => void;
  onSubDragStart?: (subId: string) => void;
  onSubDragEnd?: () => void;
  totalPoints: number;
}

export const PollConstantSumForm = ({
  clusters,
  allocations,
  subAllocations,
  onClusterChange,
  onSubChange,
  onClusterDragStart,
  onClusterDragEnd,
  onSubDragStart,
  onSubDragEnd,
  totalPoints,
}: PollConstantSumFormProps) => {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const toggleExpand = (clusterId: string, shouldExpand: boolean) => {
    setExpanded((prev) => ({ ...prev, [clusterId]: shouldExpand }));
  };

  return (
    <TooltipProvider>
      <Card className="p-4 space-y-6">
        <div>
          <h3 className="text-lg font-semibold mb-1">Allocate Your 100 Points</h3>
          <p className="text-sm text-muted-foreground">
            Distribute 100 points across clusters and their subcategories.
          </p>
          <p className="text-xs text-muted-foreground mt-1">Total allocated: {totalPoints}/100</p>
        </div>

        {/* Clusters with bordered containers */}
        <div className="space-y-4">
          {clusters.map((cluster) => {
            const clusterValue = allocations[cluster.id] || 0;
            const isExpanded = expanded[cluster.id] ?? clusterValue > 0;
            const subList = cluster.subCategories;
            const hasSubcategories = subList.length > 0;

            return (
              <div
                key={cluster.id}
                className="border border-border rounded-lg p-4 bg-card/50"
              >
                {/* Cluster row: image | chevron | content */}
                <div className="grid grid-cols-[2.75rem_1.25rem_1fr] gap-x-2 items-start">
                  {/* Image column */}
                  <div className="pt-0.5">
                    {cluster.image_url && (
                      <img src={cluster.image_url} alt={cluster.label} className="w-10 h-10 object-cover rounded-md" loading="lazy" width={40} height={40} />
                    )}
                  </div>

                  {/* Chevron column */}
                  <div className="flex items-center justify-center pt-1">
                    {hasSubcategories && (
                      <ChevronDown
                        className={cn(
                          "w-4 h-4 text-muted-foreground transition-transform duration-200",
                          isExpanded ? "rotate-180" : "rotate-0"
                        )}
                      />
                    )}
                  </div>

                  {/* Content column */}
                  <div className="space-y-3">
                    {/* Header */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1">
                        <span className="font-medium text-base">{cluster.label}</span>
                        {cluster.description && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Info className="w-4 h-4 text-muted-foreground cursor-pointer" />
                            </TooltipTrigger>
                            <TooltipContent>
                              <p className="max-w-xs text-sm">{cluster.description}</p>
                            </TooltipContent>
                          </Tooltip>
                        )}
                      </div>
                      <span className="text-sm text-muted-foreground whitespace-nowrap">{clusterValue} pts</span>
                    </div>

                    {/* Cluster slider with visible gray baseline */}
                    <ConstantSumSlider
                      value={clusterValue}
                      max={100}
                      onDragStart={() => onClusterDragStart?.(cluster.id)}
                      onChange={(val) => {
                        onClusterChange(cluster.id, val);
                        toggleExpand(cluster.id, val > 0);
                      }}
                      onDragEnd={() => onClusterDragEnd?.()}
                      className="[&>[role=track]]:!h-2
                                 [&>[role=track]]:!rounded-full
                                 [&>[role=range]]:!rounded-full
                                 [&>[role=thumb]]:!size-4
                                 [&>[role=range]]:!bg-blue-500"
                    />
                  </div>
                </div>

                {/* Subcategories (auto-expand) - same grid structure for alignment */}
                {isExpanded && subList.length > 0 && (
                  <div className="mt-4 grid grid-cols-[2.75rem_1.25rem_1fr] gap-x-2">
                    {/* Empty image column */}
                    <div />
                    {/* Vertical line column */}
                    <div className="flex justify-center">
                      <div className="w-0.5 h-full bg-muted" />
                    </div>
                    {/* Subcategories content */}
                    <div className="space-y-3">
                      {subList.map((sub) => {
                        const subValue = subAllocations[sub.id] || 0;

                        return (
                          <div key={sub.id} className="space-y-1">
                            <div className="flex items-center justify-between text-sm">
                              <div className="flex items-center gap-1">
                                <span>{sub.label}</span>
                                {sub.description && (
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Info className="w-3 h-3 text-muted-foreground cursor-pointer" />
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p className="max-w-xs text-sm">{sub.description}</p>
                                    </TooltipContent>
                                  </Tooltip>
                                )}
                              </div>
                              <span className="text-xs text-muted-foreground whitespace-nowrap">{subValue} pts</span>
                            </div>

                            {/* Sub slider container limited to cluster allocation width */}
                            <div className="relative mt-3" style={{ width: `${Math.max(clusterValue, 10)}%` }}>
                              <ConstantSumSlider
                                value={subValue}
                                max={clusterValue}
                                onDragStart={() => onSubDragStart?.(sub.id)}
                                onChange={(val) => onSubChange(sub.id, val)}
                                onDragEnd={() => onSubDragEnd?.()}
                                className="[&>[role=track]]:!bg-gray-300
                                           dark:[&>[role=track]]:!bg-gray-700
                                           [&>[role=track]]:h-2
                                           [&>[role=track]]:rounded-full
                                           [&>[role=range]]:!bg-indigo-400
                                           [&>[role=range]]:rounded-full"
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Card>
    </TooltipProvider>
  );
};
